import { RFIStatus } from '@cims-online/shared'
import { CIMSModalWrapperProps } from 'components/CIMSModal/types'
import { useRFITrackerContext } from '../../../../../../RFITrackerContext'
import {
  RequestForInformation,
  ResponseDiscipline,
} from '../../../../../../RFITrackerContext/types'
import { DraftFormItems } from './components/DraftFormItems'
import { PendingResponsesFormItems } from './components/PendingResponsesFormItems'
import {
  RevertRequestForInformationInput,
  useRevertRequestForInformationMutation,
} from 'schema'
import { toMoment } from 'utils/toMoment'
import { useForm, useWatch } from 'antd/lib/form/Form'
import WorkflowRollbackModal from 'components/CIMSModal/WorkflowRollbackModal'
import { useEffect, useState } from 'react'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { simpleRevertNotification } from 'hooks/notificationHandler'
import { Moment } from 'moment'
import { AffectedValueProps } from 'components/CIMSModal/WorkflowRollbackModal/components/AffectedValues/components/AffectedValue/types'
import { dateFormatter } from 'utils/dateFormatter'

type RevertModalFormValues = {
  status?: 'Draft' | 'Pending Responses'
  description?: string
  organizationId?: string
  requestedResponseDate?: Moment
  responseDisciplineIds?: string[]
  reopenDisciplineIds?: string[]
  additionalDisciplineIds?: string[]
  comment?: string
}

export const RevertRFIModal: React.FC<
  CIMSModalWrapperProps<RequestForInformation>
> = ({ onClose, visible, state }) => {
  const { refetch } = useRFITrackerContext()
  const [form] = useForm()
  const reopenIdsWatch = useWatch('reopenDisciplineIds', form)
  const [reopenedDisciplines, setReopenedDisciplines] = useState<
    ResponseDiscipline[]
  >([])
  const initialValues: RevertModalFormValues = {
    description: state?.description,
    organizationId: state?.organizationId ?? undefined,
    requestedResponseDate: toMoment(state?.requestedResponseDate) ?? undefined,
    responseDisciplineIds: state?.responseDisciplines?.map(({ id }) => id),
  }
  const [loading, setLoading] = useState<boolean>(false)
  const [revert] = useRevertRequestForInformationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      revertRequestForInformation: {
        requestForInformation: { formattedNumber, statusId },
      },
    }) =>
      simpleRevertNotification({
        name: formattedNumber,
        status: RFIStatus.fromId(statusId)?.name,
      }),
  })
  /**
   * Both useWatch and useState trigger re-rendering that clears validation error
   * messages in Ant D form fields, in this case "reopenDisciplineIds"
   *
   * Because of this we have to be very deliberate with the order of operations
   * here, manually triggering validation only after everything else is done updating
   */
  useEffect(() => {
    setReopenedDisciplines(
      state?.responseDisciplines?.filter(({ id }) =>
        reopenIdsWatch?.includes(id)
      ) ?? []
    )
  }, [reopenIdsWatch, state])
  useEffect(() => {
    form.validateFields(['reopenDisciplineIds', 'additionalDisciplineIds'])
  }, [reopenedDisciplines, form])

  return (
    <WorkflowRollbackModal
      type="multiple"
      loading={loading}
      onClose={onClose}
      visible={visible}
      externalForm={form}
      initialFormValues={initialValues as RevertModalFormValues}
      recordName={state?.formattedNumber}
      statuses={[
        {
          statusName: RFIStatus.Draft.name,
          formItems: <DraftFormItems {...state} />,
          affectedItems: [
            {
              label: 'Submitted By',
              value:
                state?.submittedBy?.displayName ?? state?.submittedBy?.mail,
            },
            {
              label: 'Submitted Date',
              value: dateFormatter.simple(state?.submittedDate),
            },
            ...(state?.statusId === RFIStatus.Closed.id
              ? [
                  {
                    label: 'Closed Date',
                    value: dateFormatter.simple(state?.closedDate),
                  },
                ]
              : []),
            {
              label: 'Response Disciplines',
              value: state?.responseDisciplines.reduce<AffectedValueProps[]>(
                (affectedDisciplines, discipline) => {
                  const closedResponse = state?.responses?.find(
                    ({ responseDisciplineId, closed }) =>
                      responseDisciplineId === discipline.id && closed
                  )
                  if (closedResponse)
                    affectedDisciplines.push({
                      label: discipline.name,
                      value: [
                        {
                          label: 'Closed',
                          value: 'true',
                        },
                        {
                          label: 'Closed By',
                          value:
                            closedResponse.user.displayName ??
                            closedResponse.user.mail,
                        },
                      ],
                    })
                  return affectedDisciplines
                },
                []
              ),
            },
          ],
        },
        {
          statusName: RFIStatus.PendingResponses.name,
          disabled: state?.statusId === RFIStatus.PendingResponses.id,
          formItems: <PendingResponsesFormItems {...state} form={form} />,
          affectedItems: [
            {
              label: 'Closed Date',
              value: dateFormatter.simple(state?.closedDate),
            },
            {
              label: 'Response Disciplines',
              value:
                reopenedDisciplines?.map(({ name, id }) => {
                  const closedUser = state?.responses?.find(
                    ({ responseDisciplineId }) => responseDisciplineId === id
                  )?.user
                  const closedBy = closedUser?.displayName ?? closedUser?.mail
                  return {
                    label: name,
                    value: [
                      {
                        label: 'Closed',
                        value: 'true',
                      },
                      {
                        label: 'Closed By',
                        value: closedBy,
                      },
                    ],
                  }
                }) ?? [],
            },
          ],
        },
      ]}
      onOk={async ({
        status,
        description,
        organizationId,
        requestedResponseDate,
        responseDisciplineIds,
        reopenDisciplineIds,
        additionalDisciplineIds,
        comment,
      }: RevertModalFormValues) => {
        const input = {
          id: state?.id,
          // Status is required for this callback to execute, so using bang.
          statusId: RFIStatus.fromNameOrFail(status!).id,
          description,
          organizationId,
          requestedResponseDate: requestedResponseDate?.toDate(),
          responseDisciplineIds,
          reopenDisciplineIds,
          additionalDisciplineIds,
          comment,
        } as RevertRequestForInformationInput
        setLoading(true)
        await revert({ variables: { revertRequestForInformationInput: input } })
        setLoading(false)
        refetch()
        onClose()
      }}
    />
  )
}
